import { CircleCheck, Loader2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { SaveStack } from './util_saveStack';

interface SaveStatusIndicatorProps {
  saveStack: SaveStack;
}

/**
 * Compact "saving / saved" status. Adapted from the portal's SaveStatusIndicator,
 * using lucide icons and the existing tooltip (no react-spinners / popover).
 */
export function SaveStatusIndicator({ saveStack }: SaveStatusIndicatorProps) {
  const pending = saveStack.stackItems.size;
  const showCheckmark = saveStack.showCheckmark && !!saveStack.lastSavedTimestamp;

  if (pending > 0) {
    const actions = Array.from(saveStack.stackItems.values());
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-ed-stone'>
              <Loader2 className='tw:size-4 tw:animate-spin tw:text-ed-gold' />
              Saving{pending > 1 ? ` (${pending})` : ''}…
            </span>
          </TooltipTrigger>
          <TooltipContent side='bottom'>
            <ul className='tw:flex tw:flex-col tw:gap-0.5'>
              {actions.map((item) => (
                <li key={item.id}>{item.userAction}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (showCheckmark) {
    const savedAt = saveStack.lastSavedTimestamp!.toLocaleTimeString();
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:text-ed-green'>
              <CircleCheck className='tw:size-4' />
              Saved
            </span>
          </TooltipTrigger>
          <TooltipContent side='bottom'>Saved {savedAt}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
}
